import { gql, useQuery, WatchQueryFetchPolicy } from '@apollo/client';
import { EMPTY_ARRAY } from '@modules/common/constants';
import { useShouldRemoveFileUploadPrefix } from '@modules/common/hooks/useShouldRemoveFileUploadPrefix';
import { replaceSourceNameForTenantIfRequired } from '@modules/common/utils';
import {
  IntegrationChannelType,
  ListEqlFieldValuesInput,
  Query,
  Source,
} from 'generated/graphql';
import { useCallback, useMemo } from 'react';
import { useGetUser } from 'v2/core/services/user';

const MAX_FIELD_VALUES_TO_FETCH = 100;

const LIST_EQL_FIELDS_VALUES_GQL = gql`
  query listEQLFieldValues(
    $tenantID: ID!
    $eqlField: String!
    $searchTerm: String
    $limit: Int
    $cursor: String
  ) {
    listEQLFieldValues(
      request: {
        tenantID: $tenantID
        eqlField: $eqlField
        searchTerm: $searchTerm
        limit: $limit
        cursor: $cursor
      }
    ) {
      values {
        value
        displayName
        iconURL
      }
      cursor
    }
  }
`;

interface FetchEQLFieldValuesParams {
  searchTerm?: string;
  cursor?: string;
  limit?: number;
}

interface useFetchEqlFieldValuesParams extends FetchEQLFieldValuesParams {
  eqlField: string;
  fetchPolicy?: WatchQueryFetchPolicy;
}

export const useFetchEqlFieldValues = ({
  eqlField,
  searchTerm,
  fetchPolicy = 'cache-first',
}: useFetchEqlFieldValuesParams) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();

  const { fetchMore, loading, data } = useQuery<Query, ListEqlFieldValuesInput>(
    LIST_EQL_FIELDS_VALUES_GQL,
    {
      variables: {
        tenantID,
        eqlField,
        searchTerm: searchTerm || undefined,
        limit: MAX_FIELD_VALUES_TO_FETCH,
      },
      fetchPolicy: fetchPolicy,
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
    },
  );

  const fetchEQLFieldValues = useCallback(
    async ({ searchTerm, cursor }: FetchEQLFieldValuesParams) => {
      await fetchMore({
        variables: {
          tenantID,
          eqlField,
          limit: MAX_FIELD_VALUES_TO_FETCH,
          searchTerm: searchTerm || undefined,
          cursor,
        },
      });
    },
    [fetchMore, tenantID, eqlField],
  );

  const values = useMemo(() => {
    if (shouldRemoveFileUploadPrefix) {
      return data?.listEQLFieldValues?.values.map((value) => {
        let displayName = value.displayName;

        if (value?.value.startsWith(Source.FileUpload)) {
          displayName = displayName
            .replaceAll(IntegrationChannelType.FileUpload, '')
            .trim();
        }

        return {
          ...value,
          displayName: replaceSourceNameForTenantIfRequired(displayName),
        };
      });
    }

    return data?.listEQLFieldValues?.values.map((value) => {
      return {
        ...value,
        displayName: replaceSourceNameForTenantIfRequired(value.displayName),
      };
    });
  }, [data?.listEQLFieldValues?.values, shouldRemoveFileUploadPrefix]);

  return [
    fetchEQLFieldValues,
    {
      values: values ?? EMPTY_ARRAY,
      cursor: data?.listEQLFieldValues?.cursor,
      isLoading: values?.length ? false : loading,
    },
  ] as const;
};
